"use client";

/**
 * Shared private-insurance picker building blocks.
 * Used by CoverageEstimator (the /coverage and PDP estimator) and by
 * the checkout page's insurance section so both stay on one source of
 * truth for the provider list and dropdown styling.
 */

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export type InsuranceProvider = {
  value: string;
  label: string;
  logo: string | null;
  /** Short text shown in the logo slot when no image asset is available
   *  (e.g. "WCB" for WorkSafe BC, plan tag for BC public plans). */
  badge?: string;
};

export const PRIVATE_INSURANCE_PROVIDERS: InsuranceProvider[] = [
  { value: "telus-health", label: "TELUS Health", logo: "/border-pharmacy/companyimages/Company-1.avif" },
  { value: "nexgenrx", label: "NexgenRx", logo: "/border-pharmacy/companyimages/Company-2.avif" },
  { value: "desjardins", label: "Desjardins", logo: "/border-pharmacy/companyimages/Company-3.avif" },
  { value: "express-scripts", label: "Express Scripts", logo: "/border-pharmacy/companyimages/Company-4.avif" },
  { value: "canada-life", label: "Canada Life", logo: "/border-pharmacy/companyimages/Company-5.avif" },
  { value: "sun-life", label: "Sun Life", logo: "/border-pharmacy/companyimages/Company-6.avif" },
  { value: "manulife", label: "Manulife", logo: "/border-pharmacy/companyimages/Company-7.avif" },
  { value: "pacific-blue-cross", label: "Pacific Blue Cross", logo: "/border-pharmacy/companyimages/Company-8.avif" },
  { value: "greenshield", label: "GreenShield", logo: "/border-pharmacy/companyimages/Company-9.avif" },
];

// BC PharmaCare public plans — used as primary on the checkout flow,
// since in BC the public plan adjudicates before any private insurer.
// `value` mirrors the PharmaCarePlan keys used by the coverage estimator.
export const BC_PUBLIC_PLAN_PROVIDERS: InsuranceProvider[] = [
  { value: "fair-pharmacare", label: "Fair PharmaCare (Plan I)", logo: null, badge: "I" },
  { value: "plan-c", label: "Income Assistance (Plan C)", logo: null, badge: "C" },
  { value: "plan-b", label: "Long-Term Care (Plan B)", logo: null, badge: "B" },
  { value: "plan-f", label: "At-Home Program (Plan F)", logo: null, badge: "F" },
  { value: "plan-g", label: "Mental Health (Plan G)", logo: null, badge: "G" },
  { value: "plan-p", label: "Palliative (Plan P)", logo: null, badge: "P" },
  { value: "plan-w", label: "First Nations Health (Plan W)", logo: null, badge: "W" },
];

export const WORKSAFE_BC_PROVIDER: InsuranceProvider = {
  value: "worksafe-bc",
  label: "WorkSafeBC",
  logo: null,
  badge: "WCB",
};

const OTHER_PROVIDER: InsuranceProvider = {
  value: "other",
  label: "Other (type name)",
  logo: null,
  badge: "Other",
};

// Full lookup table — every value the dropdown can produce must be
// resolvable here, no matter which subset rendered it.
export const INSURANCE_PROVIDERS: InsuranceProvider[] = [
  ...BC_PUBLIC_PLAN_PROVIDERS,
  ...PRIVATE_INSURANCE_PROVIDERS,
  WORKSAFE_BC_PROVIDER,
  OTHER_PROVIDER,
];

// Ordered list shown for every insurance slot on the checkout page and
// the coverage estimator — private insurers plus WorkSafeBC for
// work-related claims, then "Other" for anything not listed.
export const CHECKOUT_INSURANCE_PROVIDERS: InsuranceProvider[] = [
  ...PRIVATE_INSURANCE_PROVIDERS,
  WORKSAFE_BC_PROVIDER,
  OTHER_PROVIDER,
];

export function ProviderLogo({
  provider,
  size = 24,
}: {
  provider: InsuranceProvider;
  size?: number;
}) {
  if (!provider.logo) {
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-md bg-[#f0f0ee] text-[10px] font-bold uppercase tracking-wider text-[#888] dark:bg-white/10 dark:text-white/50"
        style={{ width: size * 2, height: size }}
      >
        {provider.badge ?? "Other"}
      </span>
    );
  }
  return (
    <span
      className="relative flex shrink-0 items-center justify-center rounded-md bg-white p-1 ring-1 ring-[#ebebea] dark:bg-white/95 dark:ring-white/20"
      style={{ width: size * 2, height: size }}
    >
      <Image
        src={provider.logo}
        alt={provider.label}
        fill
        sizes={`${size * 2}px`}
        style={{ objectFit: "contain", padding: 2 }}
      />
    </span>
  );
}

export function InsuranceProviderDropdown({
  value,
  onChange,
  placeholder = "Select your insurance provider",
  providers,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Override the rendered list. Defaults to the private-insurance set,
   *  which is what the coverage estimator's private-insurance panel
   *  expects. The checkout picker passes position-specific lists. */
  providers?: InsuranceProvider[];
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const options = providers ?? [...PRIVATE_INSURANCE_PROVIDERS, OTHER_PROVIDER];
  // Resolve label/logo against the full known list so values picked in
  // one slot still render correctly if the slot's option list changes.
  const selected = INSURANCE_PROVIDERS.find((p) => p.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e05574]/40 ${
          open
            ? "border-[#e05574] bg-[#fdf3f5] dark:border-[#e05574] dark:bg-[#2a1418]"
            : "border-[#ebebea] bg-white hover:border-[#d0d0ce] dark:border-white/10 dark:bg-[#0f1419] dark:hover:border-white/20"
        }`}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          {selected ? (
            <>
              <ProviderLogo provider={selected} size={20} />
              <span className="truncate text-sm font-medium text-[#111] dark:text-white">
                {selected.label}
              </span>
            </>
          ) : (
            <span className="text-sm text-[#888] dark:text-white/50">{placeholder}</span>
          )}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-[#888] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-72 overflow-auto rounded-xl border border-[#e8e8e5] bg-white p-1 shadow-xl dark:border-white/10 dark:bg-[#1a1f25]"
        >
          {options.map((opt) => {
            const isActive = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition ${
                  isActive
                    ? "bg-[#fdf3f5] dark:bg-[#2a1418]"
                    : "hover:bg-[#fafaf9] dark:hover:bg-white/5"
                }`}
              >
                <ProviderLogo provider={opt} size={20} />
                <span
                  className={`flex-1 truncate text-sm ${
                    isActive
                      ? "font-semibold text-[#b83058] dark:text-[#e8798f]"
                      : "text-[#222] dark:text-white"
                  }`}
                >
                  {opt.label}
                </span>
                {isActive && (
                  <span className="ml-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#e05574]">
                    <svg
                      className="h-2.5 w-2.5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="3.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m5 13 4 4L19 7"
                      />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Resolve the human-readable insurer name from a picker selection.
 * Returns null when the user hasn't picked anything (or picked "other"
 * but didn't type a name).
 */
export function resolveInsurerName(value: string, otherText: string): string | null {
  if (!value) return null;
  if (value === "other") {
    const trimmed = otherText.trim();
    return trimmed || null;
  }
  const provider = INSURANCE_PROVIDERS.find((p) => p.value === value);
  return provider?.label ?? null;
}
